import { createContext, useState, useContext, useEffect, ReactNode, useMemo } from 'react'
import { User, ViewAsDataProps } from 'src/mainTypes'
import { USER_ROLE } from 'src/utils/constants'
import { AuthStatus } from '@hotmart/space-lib/dist/enum'
import { ThemeMode } from '@hotmart/space-lib/dist/styles/themes/enum'
import { OwnerCollectionsPermissions, OwnerCollectionsPermissionsGroup } from '../usePosts'
import { managerRoles, viewPostsRoles } from './utils/roles'
import { ManagerRoles } from './constants'

export type ThemeProps = 'light' | 'dark'

export type UserProps = {
  authStatus: AuthStatus
  user: User
  setAuthStatus: (authStatus: AuthStatus) => void
  locale: string
  isViewAsEnabled: boolean
  avatar: string
  setIsViewAsEnabled: (isViewAsEnabled: boolean) => void
  slug: string
  theme: ThemeProps
  setUser: (user: User) => void
  setLocale: (locale: string) => void
  setAvatar: (avatar: string) => void
  setSlug: (slug: string) => void
  setTheme: (theme: ThemeProps) => void
  isOwnerOrAdminOrModerator: boolean
  isOwnerOrAdmin: boolean
  isOwner: boolean
  permissionsUser: OwnerCollectionsPermissions | undefined
  hasPermissionToManage: (group?: OwnerCollectionsPermissionsGroup) => boolean
  viewAsBar?: ViewAsDataProps
  canViewPosts: boolean
}

const UserContext = createContext({} as UserProps)

type UserProviderProps = {
  children: ReactNode
  parentUser: User
  viewAsBar?: ViewAsDataProps
}

const UserProvider = ({ children, parentUser, viewAsBar }: UserProviderProps) => {
  const [slug, setSlug] = useState('')
  const [avatar, setAvatar] = useState('')
  const [locale, setLocale] = useState('')
  const [user, setUser] = useState<User>(parentUser)
  const [theme, setTheme] = useState<ThemeProps>('light')
  const [isViewAsEnabled, setIsViewAsEnabled] = useState(false)
  const [authStatus, setAuthStatus] = useState<AuthStatus>(AuthStatus.AUTHENTICATING)

  useEffect(() => {
    if (theme === ThemeMode.DARK) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }, [theme])

  const getUserRoleByLocalStorage = () => {
    const userRoles = localStorage.getItem(USER_ROLE)

    return userRoles ? (JSON.parse(userRoles) as OwnerCollectionsPermissions) : null
  }

  const isOwnerOrAdminOrModerator = useMemo(() => {
    const userRole = getUserRoleByLocalStorage()

    if (!userRole) {
      return managerRoles.some((rule) => rule === user.permissions?.group)
    }

    return managerRoles.some((rule) => rule === userRole.group)
  }, [user])

  const isOwnerOrAdmin = useMemo(() => {
    const userRole = getUserRoleByLocalStorage()

    if (!userRole) {
      return managerRoles.some((rule) => rule === user.permissions?.group)
    }

    return [ManagerRoles.OWNER, ManagerRoles.ADMIN].some((rule) => rule === userRole.group)
  }, [user])

  const isOwner = useMemo(() => {
    const userRole = getUserRoleByLocalStorage()

    if (!userRole) {
      return managerRoles.some((rule) => rule === user.permissions?.group)
    }

    return [ManagerRoles.OWNER].some((rule) => rule === userRole.group)
  }, [user])

  const hasPermissionToManage = (group?: OwnerCollectionsPermissionsGroup) => {
    if (!group) {
      return false
    }

    return managerRoles.some((rule) => rule === group)
  }

  const permissionsUser = useMemo(() => {
    const userRole = getUserRoleByLocalStorage()

    if (!userRole) {
      return user?.permissions
    }

    return userRole
  }, [user]) as OwnerCollectionsPermissions

  const canViewPosts = useMemo(() => {
    const userRole = getUserRoleByLocalStorage()

    if (!userRole) {
      return viewPostsRoles.some((rule) => rule === permissionsUser?.group)
    }

    return viewPostsRoles.some((rule) => rule === userRole.group)
  }, [permissionsUser])

  useEffect(() => {
    setUser(parentUser)
    parentUser?.authStatus && setAuthStatus(parentUser.authStatus)
  }, [parentUser, authStatus])

  return (
    <UserContext.Provider
      value={{
        authStatus,
        setAuthStatus,
        user,
        isViewAsEnabled,
        setIsViewAsEnabled,
        avatar,
        setAvatar,
        locale,
        slug,
        theme,
        setUser,
        setLocale,
        setSlug,
        setTheme,
        isOwnerOrAdminOrModerator,
        isOwnerOrAdmin,
        isOwner,
        permissionsUser,
        hasPermissionToManage,
        viewAsBar,
        canViewPosts
      }}
    >
      {children}
    </UserContext.Provider>
  )
}

function useUser() {
  const context = useContext(UserContext)

  if (!Object.keys(context).length) {
    throw new Error('useUser must be used within a UserProvider')
  }

  return context
}

export { UserProvider, useUser }
